import React from 'react';
import Icon from '../../../common/Icon/Icon';
import { formatDate, formatDateTime, formatCurrency } from '../../../../utils';
import { BILLING_TYPE_LABELS, STATUS_LABELS, STATUS_ICON, normalizeStatus } from '../paymentDetails.constants';
import styles from './PayloadView.module.css';

interface AsaasPayloadViewProps {
  payload: Record<string, unknown>;
  currentStatus?: string;
  currentCurrency?: 'BRL' | 'USD';
}

const AsaasPayloadView: React.FC<AsaasPayloadViewProps> = ({ payload, currentStatus, currentCurrency }) => {
  const payment = (payload.payment ?? payload) as Record<string, unknown>;
  const status = normalizeStatus(currentStatus ?? (payment.status as string) ?? '');
  const statusLabel = STATUS_LABELS[status] ?? (payment.status as string);
  const statusIcon = STATUS_ICON[status] ?? { name: 'help', color: 'var(--color-solid-info)' };
  const billingType = payment.billingType as string | undefined;
  const currency = ((payment.currency as string)
    ?? (payment.currency_code as string)
    ?? currentCurrency
    ?? 'BRL').toUpperCase() as 'BRL' | 'USD';

  return (
    <div className={styles.container}>
      <Row label="ID da cobrança" value={payment.id as string} />
      {!!payment.description && <Row label="Descrição" value={payment.description as string} />}
      <Row label="Moeda" value={currency} />
      <Row label="Valor" value={formatCurrency(payment.value as number, currency)} />
      {payment.netValue != null && <Row label="Valor líquido" value={formatCurrency(payment.netValue as number, currency)} />}
      <Row
        label="Status"
        element={
          <span className={styles.status}>
            <Icon name={statusIcon.name} size={16} filled color={statusIcon.color} />
            {statusLabel}
          </span>
        }
      />
      {billingType && <Row label="Forma de pagamento" value={BILLING_TYPE_LABELS[billingType] ?? billingType} />}
      {!!payment.dateCreated && <Row label="Data de criação" value={formatDate(payment.dateCreated as string)} />}
      {!!payment.dueDate && <Row label="Vencimento" value={formatDate(payment.dueDate as string)} />}
      {!!payment.paymentDate && <Row label="Data de pagamento" value={formatDateTime(payment.paymentDate as string)} />}
      {!!payment.clientPaymentDate && <Row label="Data de pagamento (cliente)" value={formatDateTime(payment.clientPaymentDate as string)} />}
      {!!payment.creditDate && <Row label="Data de crédito" value={formatDate(payment.creditDate as string)} />}
      {!!payment.nossoNumero && <Row label="Nosso número" value={payment.nossoNumero as string} />}
      {!!payment.invoiceNumber && <Row label="Número da fatura" value={payment.invoiceNumber as string} />}

      {!!payment.invoiceUrl && (
        <Row
          label="Link da fatura"
          element={
            <a href={payment.invoiceUrl as string} target="_blank" rel="noopener noreferrer" className={styles.link}>
              <Icon name="open_in_new" size={14} />
              Abrir fatura
            </a>
          }
        />
      )}
      {!!payment.bankSlipUrl && (
        <Row
          label="Boleto"
          element={
            <a href={payment.bankSlipUrl as string} target="_blank" rel="noopener noreferrer" className={styles.link}>
              <Icon name="open_in_new" size={14} />
              Abrir boleto
            </a>
          }
        />
      )}
      {!!payment.transactionReceiptUrl && (
        <Row
          label="Comprovante"
          element={
            <a href={payment.transactionReceiptUrl as string} target="_blank" rel="noopener noreferrer" className={styles.link}>
              <Icon name="open_in_new" size={14} />
              Abrir comprovante
            </a>
          }
        />
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string; element?: React.ReactNode }> = ({ label, value, element }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    {element ?? <span className={styles.value}>{value ?? '—'}</span>}
  </div>
);

export default AsaasPayloadView;
